import React, { useState, useEffect, useCallback } from 'react';
import { X, Download, Loader2, CheckCircle } from 'lucide-react';
import { leadsApi } from '../../lib/api';
import { trackGenerateLead } from '../../lib/analytics';
import { canShowPopup, registerPopupShown, markPopupDismissed, wasPopupShown } from '../../lib/popupManager';

const PDF_URL = "https://customer-assets.emergentagent.com/job_1590f9cf-5e05-4494-9fd1-928104d1fc8f/artifacts/u1yo04ch_8%20week%20hyrox%20training%20guide.pdf";

export const HyroxProgramPopup = ({ articleSlug, forceShow, disableAutoShow = false }) => {
  const popupId = `hyrox_program_${articleSlug || 'general'}`;
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(() => wasPopupShown(popupId));
  const [formData, setFormData] = useState({ email: '', first_name: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const tryShow = useCallback(() => {
    if (dismissed) return;
    if (canShowPopup(popupId)) {
      setVisible(true);
      registerPopupShown(popupId);
    }
  }, [dismissed, popupId]);

  // Force show from parent (inline CTA click)
  useEffect(() => {
    if (forceShow) {
      setDismissed(false);
      setVisible(true);
      registerPopupShown(popupId);
    }
  }, [forceShow, popupId]);

  // Timer: show after 10 seconds
  useEffect(() => {
    if (dismissed || disableAutoShow) return;
    const timer = setTimeout(tryShow, 10000);
    return () => clearTimeout(timer);
  }, [dismissed, disableAutoShow, tryShow]);

  // Exit intent detection
  const handleMouseLeave = useCallback((e) => {
    if (dismissed || visible || disableAutoShow) return;
    if (e.clientY <= 0) tryShow();
  }, [dismissed, visible, disableAutoShow, tryShow]);

  useEffect(() => {
    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [handleMouseLeave]);

  const handleDismiss = () => {
    setVisible(false);
    setDismissed(true);
    markPopupDismissed(popupId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email) { setError('Email is required'); return; }
    setLoading(true);
    setError('');
    try {
      await leadsApi.create({
        email: formData.email,
        first_name: formData.first_name || null,
        source: `hyrox_program_download_${articleSlug}`,
        interests: ['hyrox', 'training_program', '8_week_plan'],
      });
      setSuccess(true);
      trackGenerateLead(`hyrox_program_download_${articleSlug}`);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    window.open(PDF_URL, '_blank');
    setTimeout(handleDismiss, 1500);
  };

  if (!visible || dismissed) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="hyrox-popup-overlay">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={handleDismiss} />
      
      {/* Modal */}
      <div 
        className="relative w-full max-w-lg bg-[#0a0a0a] border border-[#FFD100]/30 overflow-hidden animate-fade-in"
        data-testid="hyrox-popup-modal"
      >
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
          data-testid="hyrox-popup-close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Yellow accent bar */}
        <div className="h-1 bg-[#FFD100]" />
        
        <div className="p-8">
          {success ? (
            /* Success State */
            <div className="text-center py-4" data-testid="hyrox-popup-success">
              <CheckCircle className="w-16 h-16 text-[#FFD100] mx-auto mb-4" />
              <h3 
                className="text-3xl text-white mb-3"
                style={{ fontFamily: 'Bebas Neue, sans-serif' }}
              >
                Your Program is Ready
              </h3>
              <p className="text-zinc-400 mb-6">
                Click below to download your 8-week HYROX training plan.
              </p>
              <button
                onClick={handleDownload}
                className="w-full py-4 bg-[#FFD100] text-black font-bold uppercase tracking-wider flex items-center justify-center gap-3 hover:bg-[#FFD100]/90 transition-colors"
                style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: '1.1rem' }}
                data-testid="hyrox-popup-download"
              >
                <Download className="w-5 h-5" />
                Download 8-Week Program (PDF)
              </button>
            </div>
          ) : (
            /* Form State */
            <>
              <div className="text-center mb-6">
                <span 
                  className="inline-block px-3 py-1 text-xs uppercase tracking-wider bg-[#FFD100] text-black font-bold mb-4"
                >
                  Free Download
                </span>
                <h3 
                  className="text-3xl text-white mb-2"
                  style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                >
                  8-Week HYROX Training Program
                </h3>
                <p className="text-zinc-400 text-sm leading-relaxed">
                  Day-by-day workouts, station-specific protocols, pacing strategy, 
                  race-day checklist & equipment recommendations.
                </p>
              </div>

              {/* Program highlights */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                {['4-Phase Plan', 'Race Simulations', 'Pacing Guide', 'Benchmark Tracker'].map((item) => (
                  <div key={item} className="flex items-center gap-2 text-sm text-zinc-300">
                    <span className="text-[#FFD100]">&#10003;</span>
                    {item}
                  </div>
                ))}
              </div>

              <form onSubmit={handleSubmit} className="space-y-3" data-testid="hyrox-popup-form">
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  placeholder="First name"
                  className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 text-white text-sm placeholder-zinc-500 focus:border-[#FFD100] focus:outline-none transition-colors"
                  data-testid="hyrox-popup-name"
                />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Email address *"
                  required
                  className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 text-white text-sm placeholder-zinc-500 focus:border-[#FFD100] focus:outline-none transition-colors"
                  data-testid="hyrox-popup-email"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 bg-[#FFD100] text-black font-bold uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-[#FFD100]/90 transition-colors disabled:opacity-50"
                  style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: '1.1rem' }}
                  data-testid="hyrox-popup-submit"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Download className="w-5 h-5" />
                      Get My Free Program
                    </>
                  )}
                </button>
                {error && <p className="text-red-500 text-sm text-center">{error}</p>}
              </form>

              <p className="text-zinc-600 text-xs text-center mt-4">
                We respect your privacy. Unsubscribe anytime.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default HyroxProgramPopup;
